/**
 * Marketing first-touch attribution — the single source of truth for the shape,
 * URL-param capture map, and Customer.io / PostHog projections. Pure (no Node or
 * DOM deps) so BOTH the servers (apps/api, worker) and the browser apps
 * (frontend, marketing capture) import it via `@onark/shared/attribution`.
 *
 * Captured from URL params on the client, stored as JSON on the product DB
 * (Church.attribution = person first-touch; WelcomeAboardEnrollment.offerAttribution
 * = the WA ad that sold a specific purchase), and projected onto the Customer.io
 * person (snake_case) so the #cio-alerts entrant alerts show how each person
 * arrived. Field contract + Ads-Manager URL strings: docs/attribution-ad-url-convention.md.
 */

export interface AttributionInput {
  fbclid?: string | null;
  gclid?: string | null;
  landingPath?: string | null;
  // Referral / affiliate code that drove the signup. NOT captured from the URL
  // into the client attribution cookie (it rides its own `onark-ref` cookie and
  // the `referredByCodeId` FK). The SERVER stamps the resolved code into the
  // stored attribution blob at signup, so the referrer/affiliate surfaces in the
  // #cio-alerts entrant alert and PostHog money events alongside utm/referrer.
  // Never travels through the GraphQL AttributionInput arg — signup is unchanged.
  ref?: string | null;
  referrer?: string | null;
  utmCampaign?: string | null;
  utmContent?: string | null;
  utmMedium?: string | null;
  utmSource?: string | null;
  utmTerm?: string | null;
  // Stable platform ids for exact ad/audience matching even after a rename —
  // captured from dedicated URL params (Meta {{ad.id}}/{{adset.id}}/{{campaign.id}},
  // Google {creative}/{adgroupid}/{campaignid}).
  adId?: string | null;
  adsetId?: string | null;
  campaignId?: string | null;
}

export const ATTRIBUTION_KEYS: (keyof AttributionInput)[] = [
  'utmSource',
  'utmMedium',
  'utmCampaign',
  'utmTerm',
  'utmContent',
  'gclid',
  'fbclid',
  'referrer',
  'landingPath',
  'ref',
  'adId',
  'adsetId',
  'campaignId',
];

/**
 * camelCase field → URL query-param name, for the browser capture sites
 * (readFromUrl / FirstTouchAttribution). Only the query-derived fields —
 * `referrer` (document.referrer) and `landingPath` (window.location.pathname)
 * are derived from the DOM, not the query string, so the browser handles those
 * two directly. Adding a new ad param means editing this map only.
 */
export const ATTRIBUTION_URL_PARAMS: Partial<
  Record<keyof AttributionInput, string>
> = {
  adId: 'ad_id',
  adsetId: 'adset_id',
  campaignId: 'campaign_id',
  fbclid: 'fbclid',
  gclid: 'gclid',
  utmCampaign: 'utm_campaign',
  utmContent: 'utm_content',
  utmMedium: 'utm_medium',
  utmSource: 'utm_source',
  utmTerm: 'utm_term',
};

// A utm_*/ad-id param never legitimately contains a URL, so `://` is the tell
// that an Ads-Manager link was built wrong. When the destination URL already
// carries a tagged query AND the ad appends a second full URL, the two run
// together and the query ends up carrying every param TWICE — with the FIRST
// copy of the last param swallowing the head of the second URL:
//
//   ...&utm_content=founder_video_pcohttps://onark.app/start/pco?utm_source=…
//                   ^^^^^^^^^^^^^^^^^ intended value  ^^^^^^^^^^^^^^^^^^^^^^ garbage
//
// `URLSearchParams.get()` returns that first, corrupt copy — which then rides
// the cookie into `Church.attribution`, the Customer.io person attributes, and
// the `#cio-alerts` Slack templates, and fragments the per-ad door report into
// a phantom row. Observed in production 2026-08-11 on the PCO writeback ad.
const CORRUPT_MARKER = '://';

// An UNEXPANDED ad-platform macro (`{{ad.name}}` Meta, `{creative}` Google) is
// the same failure from the other side: the platform never substituted it, so
// the literal template text would be stored as if it were a campaign. Issue
// kuzu-media/onark-marketing-system#10 drops macro-filled names entirely, so a
// leftover macro is never a value worth keeping.
const UNEXPANDED_MACRO = /\{\{[^}]*\}\}|\{[a-z_]+\}/i;

/**
 * Trim an attribution value and reject it if it carries a URL. Exported so the
 * capture sites that DON'T read the landing query — e.g. the downsell form,
 * which prefers PostHog's persisted `$initial_utm_*` — screen those values
 * through the same rule. `posthog-js` reads the raw URL itself and does not
 * sanitize, so a concatenated link corrupts the person property too.
 *
 * Never apply this to `referrer`, which is legitimately a URL.
 */
export function usableAttributionValue(
  raw: string | null | undefined
): string | undefined {
  const value = raw?.trim();

  return value &&
    !value.includes(CORRUPT_MARKER) &&
    !UNEXPANDED_MACRO.test(value)
    ? value
    : undefined;
}

/**
 * Read the attribution query-params out of a landing URL's search string — the
 * one parser both browser capture sites use (`readFromUrl` on the frontend,
 * `FirstTouchAttribution` on the marketing app), so the corruption guard and
 * the URL-param map can't drift apart.
 *
 * `referrer` and `landingPath` are NOT read here: they come from the DOM
 * (`document.referrer` / `window.location.pathname`), and `referrer` is
 * legitimately a URL, so it must never see the `://` guard.
 */
export function readAttributionParams(
  search: string
): Partial<Record<keyof AttributionInput, string>> {
  const params = new URLSearchParams(search);
  const out: Partial<Record<keyof AttributionInput, string>> = {};

  const entries = Object.entries(ATTRIBUTION_URL_PARAMS) as [
    keyof AttributionInput,
    string,
  ][];

  for (const [key, param] of entries) {
    // Last-wins across the surviving copies: Meta appends the URL-parameters
    // field AFTER the destination URL's own query, so when both carry the same
    // param the TRAILING copy is the macro-expanded one we actually asked for.
    // That's what recovers the real value from a concatenated link instead of
    // merely dropping it.
    const value = params
      .getAll(param)
      .map(usableAttributionValue)
      .filter((raw): raw is string => Boolean(raw))
      .pop();

    if (value) out[key] = value;
  }

  return out;
}

// Returns a trimmed attribution object with only the present fields, or null if
// there's no signal at all (or the input is null/absent — so a nullable
// `attribution` mutation arg can be passed straight through) — so we never
// persist empty attribution blobs.
export function buildAttribution(
  input: AttributionInput | null | undefined
): Record<string, string> | null {
  if (!input) return null;

  const out: Record<string, string> = {};

  for (const key of ATTRIBUTION_KEYS) {
    const value = input[key]?.trim();

    if (value) out[key] = value;
  }

  return Object.keys(out).length > 0 ? out : null;
}

// Stamp a resolved referral/affiliate code onto an attribution blob, so the
// referrer that drove a signup surfaces in the CIO/PostHog projections
// (`referral_code`) alongside utm/referrer — not only as the internal
// `referredByCodeId` FK. Pass the code ONLY when it actually resolved to a
// ReferralCode; an unresolved/garbage `?ref=` must not be stamped. Null-safe:
// stamps onto an empty blob when a referred church had no other attribution.
export function withReferralCode(
  attribution: Record<string, string> | null,
  code: string | null | undefined
): Record<string, string> | null {
  const trimmed = code?.trim();

  if (!trimmed) return attribution;

  return { ...(attribution ?? {}), ref: trimmed };
}

// Flatten a stored attribution JSON blob into prefixed event properties
// (e.g. { utm_source, utm_medium, ... }) for PostHog money events. Prefix
// defaults to '' (first-touch); pass 'offer_' for offer-touch props.
export function attributionEventProps(
  attribution: unknown,
  prefix = ''
): Record<string, string> {
  if (!attribution || typeof attribution !== 'object') return {};

  const src = attribution as Record<string, unknown>;
  const out: Record<string, string> = {};

  for (const key of ATTRIBUTION_KEYS) {
    const value = src[key];

    if (typeof value === 'string' && value) out[`${prefix}${key}`] = value;
  }

  return out;
}

// snake_case names for the Customer.io PERSON attributes — the #cio-alerts Slack
// templates print these ({{customer.utm_source}}, {{customer.landing_path}}, …)
// so ops can see how each new entrant arrived (landing page, ad, referrer).
const CIO_ATTRIBUTION_KEYS: Record<keyof AttributionInput, string> = {
  adId: 'ad_id',
  adsetId: 'adset_id',
  campaignId: 'campaign_id',
  fbclid: 'fbclid',
  gclid: 'gclid',
  landingPath: 'landing_path',
  ref: 'referral_code',
  referrer: 'referrer',
  utmCampaign: 'utm_campaign',
  utmContent: 'utm_content',
  utmMedium: 'utm_medium',
  utmSource: 'utm_source',
  utmTerm: 'utm_term',
};

// Map an attribution blob to Customer.io person attributes. Returns only the
// present fields, trimmed (never blanks) — so it's safe on a raw client blob
// (the marketing /api/lead route passes one un-built) as well as on
// buildAttribution output. calling with null/empty is a no-op spread. Used at
// the identify points that feed the #cio-alerts entrant alerts.
export function attributionCioAttrs(
  attribution: unknown
): Record<string, string> {
  if (!attribution || typeof attribution !== 'object') return {};

  const src = attribution as Record<string, unknown>;
  const out: Record<string, string> = {};

  for (const key of ATTRIBUTION_KEYS) {
    const value = src[key];

    if (typeof value === 'string' && value.trim())
      out[CIO_ATTRIBUTION_KEYS[key]] = value.trim();
  }

  return out;
}
